import React, { useEffect, useRef, useState } from "react";

import { useDropzone } from "react-dropzone";
import CloudUpload from "@mui/icons-material/CloudUpload";

import axios from "axios";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
	Button,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormLabel,
	FormControlLabel,
	CircularProgress,
	IconButton,
	Snackbar,
	Checkbox,
	LinearProgress,
	Divider,
	Menu as MuiMenu,
	MenuItem as MuiMenuItem,
} from "@mui/material";
import { Add, MoreVert } from "@mui/icons-material";
import supabase from "../../supabaseClient";
import { getImageUrl } from "../../utils/imageUrl";

const PlantsTable = ({ plants, isAdmin }) => {
	const [plantToEdit, setPlantToEdit] = useState(null);
	const [plantToDelete, setPlantToDelete] = useState(null);
	const [plantToAdd, setPlantToAdd] = useState({
		plant_variety: "",
		plant_name: "",
		plant_description: "",
		pot_size: "",
		plant_type: "",
	});
	const [addPlant, setAddPlant] = useState(false);
	const [isAddingPlant, setIsAddingPlant] = useState(false);
	const [isEditingPlant, setIsEditingPlant] = useState(false);
	const [isDeletingPlant, setIsDeletingPlant] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "" });
	const [anchorEl, setAnchorEl] = useState(null);
	const [actionPlant, setActionPlant] = useState(null);
	const [selected, setSelected] = useState([]);
	const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [show401, setShow401] = useState(false);
	const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
	const [search, setSearch] = useState("");
	const [filterType, setFilterType] = useState("");
	const [createStoreItem, setCreateStoreItem] = useState(false);
	const [storeItemPrice, setStoreItemPrice] = useState("");
	const [storeItemQty, setStoreItemQty] = useState("1");
	const [storeItemReorderLevel, setStoreItemReorderLevel] = useState("0");
	const [showPhotoUpdateDialog, setShowPhotoUpdateDialog] = useState(false);
	const [photoSessionPlantIds, setPhotoSessionPlantIds] = useState([]);
	const [updatedPhotoPlantIds, setUpdatedPhotoPlantIds] = useState([]);
	const [skippedPhotoPlantIds, setSkippedPhotoPlantIds] = useState([]);
	const [replacementPhoto, setReplacementPhoto] = useState(null);
	const [replacementPhotoPreview, setReplacementPhotoPreview] = useState("");
	const [isReplacingPhoto, setIsReplacingPhoto] = useState(false);
	const photoInputRef = useRef(null);

	// Filter and search logic
	const filteredPlants = plants.filter((plant) => {
		const matchesSearch =
			plant.plant_variety.toLowerCase().includes(search.toLowerCase()) ||
			plant.plant_name.toLowerCase().includes(search.toLowerCase()) ||
			plant.plant_description.toLowerCase().includes(search.toLowerCase());
		const matchesType = filterType ? plant.plant_type === filterType : true;
		return matchesSearch && matchesType;
	});
	const photoSessionPlants = photoSessionPlantIds
		.map((plantId) => plants.find((plant) => plant.id === plantId))
		.filter(Boolean);
	const processedPhotoPlantIds = new Set([
		...updatedPhotoPlantIds,
		...skippedPhotoPlantIds,
	]);
	const currentPhotoPlant =
		photoSessionPlants.find((plant) => !processedPhotoPlantIds.has(plant.id)) ||
		null;
	const totalSessionPlants = photoSessionPlants.length;
	const processedSessionPlants = processedPhotoPlantIds.size;
	const remainingSessionPlants = totalSessionPlants - processedSessionPlants;
	const updateProgress = totalSessionPlants
		? (processedSessionPlants / totalSessionPlants) * 100
		: 0;

	useEffect(() => {
		return () => {
			if (replacementPhotoPreview) {
				URL.revokeObjectURL(replacementPhotoPreview);
			}
		};
	}, [replacementPhotoPreview]);

	//react dropzone setup
	const onDrop = (acceptedFiles) => {
		if (acceptedFiles && acceptedFiles.length > 0) {
			setSelectedFile(acceptedFiles[0]);
		}
	};
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			"image/*": [],
		},
		maxFiles: 1,
		maxSize: 5 * 1024 * 1024, // 5 MB
		multiple: false,
		disabled: isAddingPlant || isEditingPlant,
	});

	// Bulk actions
	const handleBulkAction = async (action) => {
		if (selected.length === 0) return;
		if (action === "delete") {
			setShowBulkDeleteConfirm(true);
			setBulkMenuAnchor(null);
			return;
		}
		setBulkMenuAnchor(null);
	};

	const handleBulkDeleteConfirm = async () => {
		setIsDeletingPlant(true);
		const { error } = await supabase.from("plants").delete().in("id", selected);
		if (!error) {
			setSnackbar({ open: true, message: "Deleted selected plants" });
			// Remove deleted from UI
			selected.forEach((id) => {
				const idx = plants.findIndex((p) => p.id === id);
				if (idx !== -1) plants.splice(idx, 1);
			});
			setSelected([]);
		} else {
			setSnackbar({ open: true, message: "Error deleting plants" });
		}
		setIsDeletingPlant(false);
		setShowBulkDeleteConfirm(false);
	};

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			setSelected(plants.map((p) => p.id));
		} else {
			setSelected([]);
		}
	};

	const handleAddPlant = async () => {
		setIsAddingPlant(true);
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setIsAddingPlant(false);
			return;
		}
		if (!selectedFile) {
			alert("Please select a file");
			setIsAddingPlant(false);
			return;
		}
		const formData = new FormData();
		formData.append("plant_photo", selectedFile);
		formData.append("plant_variety", plantToAdd.plant_variety);
		formData.append("plant_name", plantToAdd.plant_name);
		formData.append("plant_description", plantToAdd.plant_description);
		formData.append("pot_size", plantToAdd.pot_size);
		formData.append("plant_type", plantToAdd.plant_type);
		formData.append("supabase_id", session.user.id);

		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/add-plant`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);
			if (response.status === 200) {
				const newPlantId = response.data?.plantData?.id;
				if (createStoreItem && newPlantId) {
					const priceCents = Math.round(Number(storeItemPrice || "0") * 100);
					const title =
						[plantToAdd.plant_variety, plantToAdd.plant_name]
							.filter(Boolean)
							.join(" — ") || plantToAdd.plant_name;
					const { data: storeItemData, error: storeItemError } =
						await supabase
							.from("store_items")
							.insert([
								{
									type: "in_stock",
									title,
									description: plantToAdd.plant_description || null,
									price_cents: priceCents,
									quantity_on_hand: parseInt(storeItemQty, 10) || 0,
									reorder_level: parseInt(storeItemReorderLevel, 10) || 0,
									active: true,
								},
							])
							.select()
							.single();
					if (storeItemError) {
						alert(
							`Plant added but failed to create store item: ${storeItemError.message}`,
						);
					} else {
						const { error: linkError } = await supabase
							.from("store_item_plants")
							.insert([
								{
									store_item_id: storeItemData.id,
									plant_id: newPlantId,
								},
							]);
						if (linkError) {
							alert(
								`Plant and store item created but failed to link them: ${linkError.message}`,
							);
						}
					}
				}
				setAddPlant(false);
				setCreateStoreItem(false);
				setStoreItemPrice("");
				setStoreItemQty("1");
				setStoreItemReorderLevel("0");
				window.location.reload();
			} else {
				// Handle error response
				setIsAddingPlant(false);
				alert("Failed to add plant");
			}
		} catch (error) {
			setIsAddingPlant(false);
			if (error?.response?.data === "User not authorized to add plants") {
				alert("User not authorized to add plants");
				return;
			}
			alert(`Failed to add plant. ${error?.response?.data?.error}`);
		}
	};

	const handleEditPlant = async () => {
		setIsEditingPlant(true);
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setIsEditingPlant(false);
			return;
		}

		const formData = new FormData();
		if (selectedFile) {
			formData.append("plant_photo", selectedFile);
		}
		formData.append("plant_variety", plantToEdit.plant_variety);
		formData.append("plant_name", plantToEdit.plant_name);
		formData.append("plant_description", plantToEdit.plant_description);
		formData.append("pot_size", plantToEdit.pot_size);
		formData.append("plant_type", plantToEdit.plant_type);
		formData.append("supabase_id", session.user.id);
		formData.append("id", plantToEdit.id);
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-plant`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);
			if (response.status === 200) {
				alert("Plant edited successfully");
				setIsEditingPlant(false);
				setPlantToEdit(null);
				window.location.reload();
			} else {
				// Handle error response
				setIsEditingPlant(false);
				alert("Failed to edit plant");
			}
		} catch (error) {
			setIsEditingPlant(false);
			if (error?.response?.data === "User not authorized to edit plants") {
				alert("User not authorized to edit plants");
				return;
			}
			console.log(error);
			alert(`Failed to edit plant. ${error?.response?.data?.error}`);
		}
	};

	const handleDeletePlant = async () => {
		setIsDeletingPlant(true);
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setIsDeletingPlant(false);
			return;
		}
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-plant`,
				{
					id: plantToDelete.id,
					supabaseId: session.user.id,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);
			if (response.status === 200) {
				alert("Plant deleted successfully");
				setPlantToDelete(null);
				window.location.reload();
			} else {
				alert("Failed to delete plant");
			}
		} catch (error) {
			setIsDeletingPlant(false);
			alert(`Failed to delete plant. ${error?.response?.data?.error}`);
		}
	};

	const handleSelectOne = (id) => {
		setSelected((prev) =>
			prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
		);
	};

	const handleActionMenuOpen = (event, plant) => {
		setAnchorEl(event.currentTarget);
		setActionPlant(plant);
	};

	const handleActionMenuClose = () => {
		setAnchorEl(null);
		setActionPlant(null);
	};

	const handleBulkMenuOpen = (event) => {
		setBulkMenuAnchor(event.currentTarget);
	};

	const handleBulkMenuClose = () => {
		setBulkMenuAnchor(null);
	};

	const handleViewPhoto = (url) => {
		window.open(url, "_blank");
	};

	const clearReplacementPhoto = () => {
		setReplacementPhoto(null);
		setReplacementPhotoPreview((previousUrl) => {
			if (previousUrl) {
				URL.revokeObjectURL(previousUrl);
			}
			return "";
		});
		if (photoInputRef.current) {
			photoInputRef.current.value = "";
		}
	};

	const handleStartPhotoUpdateSession = () => {
		const allPlantIds = plants.map((plant) => plant.id);
		if (allPlantIds.length === 0) {
			setSnackbar({
				open: true,
				message: "No plants available to update.",
			});
			return;
		}
		setPhotoSessionPlantIds(allPlantIds);
		setUpdatedPhotoPlantIds([]);
		setSkippedPhotoPlantIds([]);
		clearReplacementPhoto();
		setShowPhotoUpdateDialog(true);
	};

	const handleSkipPlantPhoto = () => {
		if (!currentPhotoPlant) return;
		setSkippedPhotoPlantIds((previous) => [...previous, currentPhotoPlant.id]);
		clearReplacementPhoto();
	};

	const handleReplacementPhotoChange = (event) => {
		const selectedPhoto = event.target.files?.[0];
		if (!selectedPhoto) return;
		setReplacementPhoto(selectedPhoto);
		setReplacementPhotoPreview((previousUrl) => {
			if (previousUrl) {
				URL.revokeObjectURL(previousUrl);
			}
			return URL.createObjectURL(selectedPhoto);
		});
	};

	const handleConfirmPhotoReplace = async () => {
		if (!currentPhotoPlant || !replacementPhoto) return;
		setIsReplacingPhoto(true);
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setIsReplacingPhoto(false);
			return;
		}
		const formData = new FormData();
		formData.append("plant_photo", replacementPhoto);
		formData.append("plant_variety", currentPhotoPlant.plant_variety || "");
		formData.append("plant_name", currentPhotoPlant.plant_name || "");
		formData.append(
			"plant_description",
			currentPhotoPlant.plant_description || "",
		);
		formData.append("pot_size", currentPhotoPlant.pot_size || "");
		formData.append("plant_type", currentPhotoPlant.plant_type || "");
		formData.append("supabase_id", session.user.id);
		formData.append("id", currentPhotoPlant.id);
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-plant`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);
			if (response.status === 200) {
				setUpdatedPhotoPlantIds((previous) => [...previous, currentPhotoPlant.id]);
				setSnackbar({
					open: true,
					message: `Updated photo for "${currentPhotoPlant.plant_name}"`,
				});
				clearReplacementPhoto();
			} else {
				setSnackbar({
					open: true,
					message: `Failed to update photo for "${currentPhotoPlant.plant_name}"`,
				});
			}
		} catch (error) {
			const status = error?.response?.status;
			const serverMessage = error?.response?.data?.error || error?.response?.data;
			setSnackbar({
				open: true,
				message:
					serverMessage ||
					`Failed to update photo for "${currentPhotoPlant.plant_name}"${
						status ? ` (status ${status})` : ""
					}`,
			});
		} finally {
			setIsReplacingPhoto(false);
		}
	};

	if (show401) {
		return (
			<Box p={2}>
				<Typography color="error">
					Unauthorized. Please log in again.
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Box display="flex" flexWrap="wrap" alignItems="center" gap={2} p={2}>
				<Typography variant="h5" sx={{ minWidth: 120 }}>
					Plants
				</Typography>
				{isAdmin && (
					<IconButton
						color="primary"
						onClick={() => setAddPlant(true)}
						sx={{ ml: 1 }}
						aria-label="Add Plant"
					>
						<Add />
					</IconButton>
				)}
				<TextField
					label="Search"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					size="small"
					sx={{ minWidth: 180, flex: 1, maxWidth: 250 }}
				/>
				<FormControl size="small" sx={{ minWidth: 160 }}>
					<InputLabel id="filter-type-label">Filter by Type</InputLabel>
					<Select
						labelId="filter-type-label"
						value={filterType}
						onChange={(e) => setFilterType(e.target.value)}
						label="Filter by Type"
					>
						<MenuItem value="">All Types</MenuItem>
						<MenuItem value="Annual">Annual</MenuItem>
						<MenuItem value="Vegetable">Vegetable</MenuItem>
						<MenuItem value="Tree">Tree</MenuItem>
						<MenuItem value="Grass">Grass</MenuItem>
						<MenuItem value="Small Fruit">Small Fruit</MenuItem>
						<MenuItem value="Herb">Herb</MenuItem>
					</Select>
				</FormControl>
				{isAdmin && (
					<>
						<Button
							variant="contained"
							onClick={handleStartPhotoUpdateSession}
							sx={{ minWidth: 140 }}
						>
							Update Photos
						</Button>
						<Button
							variant="outlined"
							disabled={selected.length === 0}
							onClick={handleBulkMenuOpen}
							sx={{ minWidth: 120 }}
						>
							Bulk Actions
						</Button>
						<MuiMenu
							anchorEl={bulkMenuAnchor}
							open={Boolean(bulkMenuAnchor)}
							onClose={handleBulkMenuClose}
						>
							<MuiMenuItem onClick={() => handleBulkAction("delete")}>
								Delete
							</MuiMenuItem>
						</MuiMenu>
						<Dialog
							open={showBulkDeleteConfirm}
							onClose={() => setShowBulkDeleteConfirm(false)}
						>
							<DialogTitle>Confirm Delete</DialogTitle>
							<DialogContent>
								<Typography>
									Are you sure you want to delete {selected.length} plant
									{selected.length > 1 ? "s" : ""}?
								</Typography>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={() => setShowBulkDeleteConfirm(false)}
									variant="outlined"
								>
									Cancel
								</Button>
								<Button
									onClick={handleBulkDeleteConfirm}
									color="error"
									variant="contained"
									disabled={isDeletingPlant}
								>
									{isDeletingPlant ? <CircularProgress size={20} /> : "Delete"}
								</Button>
							</DialogActions>
						</Dialog>
					</>
				)}
			</Box>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							{isAdmin && (
								<TableCell padding="checkbox">
									<Checkbox
										checked={
											selected.length === plants.length && plants.length > 0
										}
										indeterminate={
											selected.length > 0 && selected.length < plants.length
										}
										onChange={handleSelectAll}
									/>
								</TableCell>
							)}
							<TableCell>Variety</TableCell>
							<TableCell>Name</TableCell>
							<TableCell>Description</TableCell>
							<TableCell>Type</TableCell>
							<TableCell>Pot Size</TableCell>
							{isAdmin && <TableCell>Actions</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredPlants?.map((plant) => (
							<TableRow key={plant.id} selected={selected.includes(plant.id)}>
								{isAdmin && (
									<TableCell padding="checkbox">
										<Checkbox
											checked={selected.includes(plant.id)}
											onChange={() => handleSelectOne(plant.id)}
										/>
									</TableCell>
								)}
								<TableCell>{plant.plant_variety}</TableCell>
								<TableCell>{plant.plant_name}</TableCell>
								<TableCell>{plant.plant_description}</TableCell>
								<TableCell>{plant.plant_type}</TableCell>
								<TableCell>{plant.pot_size}</TableCell>

								{isAdmin && (
									<TableCell>
										<IconButton
											onClick={(e) => handleActionMenuOpen(e, plant)}
											size="small"
										>
											<MoreVert />
										</IconButton>
										<MuiMenu
											anchorEl={anchorEl}
											open={Boolean(anchorEl) && actionPlant?.id === plant.id}
											onClose={handleActionMenuClose}
										>
											<MuiMenuItem
												onClick={() => {
													setPlantToEdit(plant);
													handleActionMenuClose();
												}}
											>
												Edit
											</MuiMenuItem>
											<MuiMenuItem
												onClick={() => {
													handleViewPhoto(plant.photo_url);
													handleActionMenuClose();
												}}
											>
												View Photo
											</MuiMenuItem>
											<MuiMenuItem
												onClick={() => {
													setPlantToDelete(plant);
													handleActionMenuClose();
												}}
											>
												Delete
											</MuiMenuItem>
										</MuiMenu>
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Add Plant Dialog */}
			{addPlant && (
				<Dialog
					open={Boolean(addPlant)}
					onClose={() => {
						setAddPlant(false);
						setCreateStoreItem(false);
						setStoreItemPrice("");
						setStoreItemQty("1");
						setStoreItemReorderLevel("0");
					}}
				>
					<DialogTitle>Add Plant</DialogTitle>
					<DialogContent>
						<Box display="flex" flexDirection="column">
							<Box p={1}>
								<TextField
									label="Plant Variety"
									value={plantToAdd?.plant_variety || ""}
									sx={{ width: "100%" }}
									onChange={(e) =>
										setPlantToAdd({
											...plantToAdd,
											plant_variety: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<TextField
									label="Plant Name"
									value={plantToAdd?.plant_name || ""}
									sx={{ width: "100%" }}
									onChange={(e) =>
										setPlantToAdd({
											...plantToAdd,
											plant_name: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<TextField
									label="Plant Description"
									sx={{ width: "100%" }}
									value={plantToAdd?.plant_description || ""}
									onChange={(e) =>
										setPlantToAdd({
											...plantToAdd,
											plant_description: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<TextField
									label="Pot Size"
									sx={{ width: "100%" }}
									value={plantToAdd?.pot_size || ""}
									onChange={(e) =>
										setPlantToAdd({
											...plantToAdd,
											pot_size: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<FormControl fullWidth>
									<InputLabel id="plant-type-label">Plant Type</InputLabel>
									<Select
										labelId="plant-type-label"
										value={plantToAdd?.plant_type || ""}
										onChange={(e) =>
											setPlantToAdd({
												...plantToAdd,
												plant_type: e.target.value,
											})
										}
									>
										<MenuItem value="Annual">Annual</MenuItem>
										<MenuItem value="Perennial">Perennial</MenuItem>
										<MenuItem value="Tree">Tree</MenuItem>
										<MenuItem value="Shrub">Shrub</MenuItem>
										<MenuItem value="Grass">Grass</MenuItem>
										<MenuItem value="Herb">Herb</MenuItem>
										<MenuItem value="Vegetable">Vegetable</MenuItem>
										<MenuItem value="Small Fruit">Small Fruit</MenuItem>
									</Select>
								</FormControl>
								</Box>
							<Box p={1}>
								<FormLabel component="legend">Plant Photo</FormLabel>
								<Box
									{...getRootProps()}
									sx={{
										border: "2px dashed #ccc",
										borderRadius: 2,
										p: 2,
										textAlign: "center",
										cursor: isAddingPlant ? "not-allowed" : "pointer",
										backgroundColor: isDragActive ? "#f0f0f0" : "inherit",
									}}
								>
									<input {...getInputProps()} />
									{selectedFile ? (
										<Typography>{selectedFile.name}</Typography>
									) : isDragActive ? (
										<Typography>Drop the file here ...</Typography>
									) : (
										<Box
											display="flex"
											flexDirection="column"
											alignItems="center"
										>
											<CloudUpload sx={{ fontSize: 40, mb: 1 }} />
											<Typography>
												Drag 'n' drop a photo here, or click to select one
											</Typography>
										</Box>
									)}
								</Box>
							</Box>
						</Box>
						<Divider sx={{ my: 1 }} />
						<Box p={1}>
							<FormControlLabel
								control={
									<Checkbox
										checked={createStoreItem}
										onChange={(e) =>
											setCreateStoreItem(e.target.checked)
										}
									/>
								}
								label="Create a store item for this plant"
							/>
						</Box>
						{createStoreItem && (
							<Box display="flex" flexDirection="column">
								<Box p={1}>
									<TextField
										label="Price ($)"
										type="number"
										inputProps={{ min: 0, step: "0.01" }}
										value={storeItemPrice}
										onChange={(e) =>
											setStoreItemPrice(e.target.value)
										}
										sx={{ width: "100%" }}
										placeholder="0.00"
									/>
								</Box>
								<Box p={1}>
									<TextField
										label="Quantity on Hand"
										type="number"
										inputProps={{ min: 0, step: 1 }}
										value={storeItemQty}
										onChange={(e) =>
											setStoreItemQty(e.target.value)
										}
										sx={{ width: "100%" }}
									/>
								</Box>
								<Box p={1}>
									<TextField
										label="Reorder Level"
										type="number"
										inputProps={{ min: 0, step: 1 }}
										value={storeItemReorderLevel}
										onChange={(e) =>
											setStoreItemReorderLevel(
												e.target.value,
											)
										}
										sx={{ width: "100%" }}
										helperText="Alert when quantity falls at or below this level"
									/>
								</Box>
							</Box>
						)}
					</DialogContent>
					<DialogActions>
						{isAddingPlant ? (
							<Button variant="contained" disabled>
								<CircularProgress size={24} />
							</Button>
						) : (
							<Button variant="contained" onClick={handleAddPlant}>
								Add
							</Button>
						)}
						<Button
							variant="outlined"
							onClick={() => {
								setAddPlant(false);
								setCreateStoreItem(false);
								setStoreItemPrice("");
								setStoreItemQty("1");
								setStoreItemReorderLevel("0");
							}}
						>
							Close
						</Button>
					</DialogActions>
				</Dialog>
			)}

			{/* Edit Plant Dialog */}
			{plantToEdit && (
				<Dialog
					open={Boolean(plantToEdit)}
					onClose={() => setPlantToEdit(null)}
				>
					<DialogTitle>Edit Plant</DialogTitle>
					<DialogContent>
						<Box display="flex" flexDirection="column">
							<Box p={1}>
								<TextField
									label="Plant Variety"
									value={plantToEdit?.plant_variety || ""}
									sx={{ width: "100%" }}
									onChange={(e) =>
										setPlantToEdit({
											...plantToEdit,
											plant_variety: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<TextField
									label="Plant Name"
									value={plantToEdit?.plant_name || ""}
									sx={{ width: "100%" }}
									onChange={(e) =>
										setPlantToEdit({
											...plantToEdit,
											plant_name: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<TextField
									label="Plant Description"
									sx={{ width: "100%" }}
									value={plantToEdit?.plant_description || ""}
									onChange={(e) =>
										setPlantToEdit({
											...plantToEdit,
											plant_description: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<TextField
									label="Pot Size"
									sx={{ width: "100%" }}
									value={plantToEdit?.pot_size || ""}
									onChange={(e) =>
										setPlantToEdit({
											...plantToEdit,
											pot_size: e.target.value,
										})
									}
								/>
							</Box>
							<Box p={1}>
								<FormControl fullWidth>
									<InputLabel id="plant-type-label">Plant Type</InputLabel>
									<Select
										labelId="plant-type-label"
										value={plantToEdit?.plant_type || ""}
										onChange={(e) =>
											setPlantToEdit({
												...plantToEdit,
												plant_type: e.target.value,
											})
										}
									>
										<MenuItem value="Annual">Annual</MenuItem>
										<MenuItem value="Perennial">Perennial</MenuItem>
										<MenuItem value="Tree">Tree</MenuItem>
										<MenuItem value="Shrub">Shrub</MenuItem>
										<MenuItem value="Grass">Grass</MenuItem>
										<MenuItem value="Herb">Herb</MenuItem>
										<MenuItem value="Vegetable">Vegetable</MenuItem>
										<MenuItem value="Small Fruit">Small Fruit</MenuItem>
									</Select>
								</FormControl>
							</Box>
							<Box p={1}>
								<FormLabel component="legend">Plant Photo</FormLabel>
								<Box
									{...getRootProps()}
									sx={{
										border: "2px dashed #ccc",
										borderRadius: 2,
										p: 2,
										textAlign: "center",
										cursor: isEditingPlant ? "not-allowed" : "pointer",
										backgroundColor: isDragActive ? "#f0f0f0" : "inherit",
									}}
								>
									<input {...getInputProps()} />
									{selectedFile ? (
										<Typography>{selectedFile.name}</Typography>
									) : isDragActive ? (
										<Typography>Drop the file here ...</Typography>
									) : (
										<Box
											display="flex"
											flexDirection="column"
											alignItems="center"
										>
											<CloudUpload sx={{ fontSize: 40, mb: 1 }} />
											<Typography>
												Drag 'n' drop a photo here, or click to select one
											</Typography>
										</Box>
									)}
								</Box>
							</Box>
						</Box>
					</DialogContent>
					<DialogActions>
						{isEditingPlant ? (
							<Button variant="contained" disabled>
								<CircularProgress size={24} />
							</Button>
						) : (
							<Button variant="contained" onClick={handleEditPlant}>
								Save
							</Button>
						)}
						<Button variant="outlined" onClick={() => setPlantToEdit(null)}>
							Cancel
						</Button>
					</DialogActions>
				</Dialog>
			)}

			{/* Delete Plant Dialog */}
			{plantToDelete && (
				<Dialog
					open={Boolean(plantToDelete)}
					onClose={() => setPlantToDelete(null)}
				>
					<DialogTitle>Delete Plant</DialogTitle>
					<DialogContent>
						<Typography>Are you sure you want to delete this plant?</Typography>
					</DialogContent>
					<DialogActions>
						{isDeletingPlant ? (
							<Button variant="contained" disabled>
								<CircularProgress size={24} />
							</Button>
						) : (
							<Button onClick={handleDeletePlant}>Delete</Button>
						)}
						<Button variant="outlined" onClick={() => setPlantToDelete(null)}>
							Cancel
						</Button>
					</DialogActions>
				</Dialog>
			)}

			<Dialog
				open={showPhotoUpdateDialog}
				onClose={() => setShowPhotoUpdateDialog(false)}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle>Update Photos</DialogTitle>
				<DialogContent>
					<Box mb={3} mt={1}>
						<Box display="flex" justifyContent="space-between" mb={1}>
							<Typography variant="body2">
								{remainingSessionPlants} remaining of {totalSessionPlants}
							</Typography>
							<Typography variant="body2">
								{Math.round(updateProgress)}% complete
							</Typography>
						</Box>
						<LinearProgress variant="determinate" value={updateProgress} />
					</Box>
					{!currentPhotoPlant ? (
						<Typography>
							Photo review complete. You have updated or skipped all in stock
							plants.
						</Typography>
					) : (
						<Box>
							<Typography variant="h6" gutterBottom>
								{currentPhotoPlant.plant_variety} {currentPhotoPlant.plant_name}
							</Typography>
							<Box
								display="grid"
								gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
								gap={2}
								mb={2}
							>
								<Box>
									<Typography variant="subtitle2" mb={1}>
										Current Photo
									</Typography>
									{currentPhotoPlant.photo_url ? (
										<Box
											component="img"
											src={getImageUrl(currentPhotoPlant.photo_url)}
											alt={`${currentPhotoPlant.plant_name} current`}
											sx={{
												width: "100%",
												maxHeight: 260,
												objectFit: "contain",
												borderRadius: 1,
												border: "1px solid #ddd",
											}}
										/>
									) : (
										<Typography color="text.secondary">
											No current photo available.
										</Typography>
									)}
								</Box>
								<Box>
									<Typography variant="subtitle2" mb={1}>
										New Photo
									</Typography>
									{replacementPhotoPreview ? (
										<Box
											component="img"
											src={replacementPhotoPreview}
											alt={`${currentPhotoPlant.plant_name} replacement`}
											sx={{
												width: "100%",
												maxHeight: 260,
												objectFit: "contain",
												borderRadius: 1,
												border: "1px solid #ddd",
											}}
										/>
									) : (
										<Typography color="text.secondary">
											Capture a replacement photo to preview it here.
										</Typography>
									)}
								</Box>
							</Box>
							<input
								ref={photoInputRef}
								type="file"
								accept="image/*"
								capture="environment"
								onChange={handleReplacementPhotoChange}
								style={{ display: "none" }}
							/>
							<Box display="flex" gap={1} flexWrap="wrap">
								<Button
									variant="outlined"
									onClick={() => photoInputRef.current?.click()}
								>
									Replace Photo
								</Button>
								<Button
									variant="contained"
									onClick={handleConfirmPhotoReplace}
									disabled={!replacementPhoto || isReplacingPhoto}
								>
									{isReplacingPhoto ? (
										<CircularProgress size={20} color="inherit" />
									) : (
										"Confirm Replace"
									)}
								</Button>
								<Button variant="text" onClick={handleSkipPlantPhoto}>
									Skip
								</Button>
							</Box>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						variant="outlined"
						onClick={() => {
							clearReplacementPhoto();
							setShowPhotoUpdateDialog(false);
						}}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={2000}
				onClose={() => setSnackbar({ open: false, message: "" })}
				message={snackbar.message}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			/>
		</Box>
	);
};

export default PlantsTable;
